import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, obj = user
  const [ready, setReady] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Handle session expiration from axios interceptor
  useEffect(() => {
    const handler = () => {
      setUser(false);
      toast.error("Oturum süreniz doldu", {
        description: "Lütfen tekrar giriş yapın.",
      });
      if (typeof window !== "undefined") {
        window.location.assign("/giris");
      }
    };
    window.addEventListener("session-expired", handler);
    return () => window.removeEventListener("session-expired", handler);
  }, []);

  // Auto-logout after 1 hour of inactivity (mouse/keyboard/touch).
  useEffect(() => {
    if (!user || user === false) return;
    const IDLE_MS = 60 * 60 * 1000; // 1 hour
    let timer;
    const resetTimer = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(async () => {
        try {
          await api.post("/auth/logout");
        } catch (_) {}
        setUser(false);
        toast.error("Oturum süreniz doldu (inaktivite)", {
          description: "1 saat işlem yapılmadığı için otomatik çıkış yapıldı.",
        });
        window.location.assign("/giris");
      }, IDLE_MS);
    };
    const events = ["mousedown", "keydown", "touchstart", "scroll", "click"];
    events.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      window.clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, resetTimer));
    };
  }, [user]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const register = async (name, email, password, consents = {}) => {
    const { data } = await api.post("/auth/register", {
      name,
      email,
      password,
      terms_accepted: !!consents.terms,
      privacy_accepted: !!consents.privacy,
      kvkk_accepted: !!consents.kvkk,
      cookie_accepted: !!consents.cookie,
    });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {}
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export function formatApiError(detail) {
  if (detail == null) return "Bir hata oluştu.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
